// src/components/learning/useCourseNavigator.js
import { useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Build a route for a lesson or exam.
 */
const buildRoute = (item) => {
  if (item.kind === "lesson") return `/learn/subcourse/course/${item.id}`;
  return `/learn/exam/${item.id}`;
};

/**
 * Flatten lessons + exams from a single course.
 */
export const normalizeCourseContents = (coursesDetail) => {
  if (!coursesDetail?.contents?.length) return [];

  return coursesDetail.contents
    .map((raw, idx) => {
      // const kind = raw.kind || raw.type || (raw.is_exam ? "exam" : "lesson");
      return {
        id: raw.id,
        kind: raw.contentType,
        title: raw.title || raw.name || `Item ${idx + 1}`,
        order: raw.order ?? idx + 1,
        route: buildRoute({ id: raw.id, kind: raw.contentType }),
      };
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/**
 * Hook for navigation within and between courses.
 *
 * @param {Object} params
 * @param {Object} params.coursesDetail - Current course detail (with lessons/exams)
 * @param {string|number} params.currentId - Current lesson/exam ID
 * @param {'lesson'|'exam'} params.currentKind - Type of the current content
 * @param {Array} params.allCourses - From useCoursesQuery() (list of all courses)
 * @param {string|number} params.currentCourseId - Current course ID
 */
export const useCourseNavigator = ({
  coursesDetail,
  currentId,
  allCourses = [],
  currentCourseId,
}) => {
  const navigate = useNavigate();

  // Flatten the current course content sequence
  const sequence = useMemo(
    () => normalizeCourseContents(coursesDetail),
    [coursesDetail]
  );

  // Find current item index
  const index = useMemo(() => {
    if (!sequence.length) return -1;
    return sequence.findIndex((it) => String(it.id) === String(currentId));
  }, [sequence, currentId]);

  const prevItem = index > 0 ? sequence[index - 1] : null;
  const nextItem =
    index >= 0 && index < sequence.length - 1 ? sequence[index + 1] : null;
  const isEndOfCourse = !nextItem && index >= 0;

  // 🔹 Determine next course when current course ends
  const nextCourseId = useMemo(() => {
    if (!isEndOfCourse || !allCourses?.length) return null;

    const currentIndex = allCourses.findIndex(
      (c) => String(c.id) === String(currentCourseId)
    );
    if (currentIndex >= 0 && currentIndex < allCourses.length - 1) {
      return allCourses[currentIndex + 1]?.id || null;
    }
    return null;
  }, [isEndOfCourse, allCourses, currentCourseId]);

  const goPrev = useCallback(() => {
    if (prevItem) navigate(prevItem.route);
  }, [navigate, prevItem]);

  const goNext = useCallback(() => {
    if (nextItem) {
      navigate(nextItem.route);
    } else if (isEndOfCourse && nextCourseId) {
      navigate(`/learn/subcourse/${nextCourseId}`);
    } else if (isEndOfCourse) {
      // fallback if no next course exists
      navigate("/learn");
    }
  }, [navigate, nextItem, isEndOfCourse, nextCourseId]);

  // Optional: keyboard shortcuts ← / →
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft" && prevItem) goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, prevItem]);

  return {
    sequence,
    index,
    prevItem,
    nextItem,
    isEndOfCourse,
    nextCourseId,
    goPrev,
    goNext,
  };
};
